import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

/**
 * Wrapper component for handling Google OAuth redirects
 * This component is used when users return from Google OAuth flow
 */
const GoogleOAuth: React.FC = () => {
  const { isAuthenticated, reload } = useAuth();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      console.log('[GOOGLE-OAUTH] 🔍 Starting OAuth callback processing...');
      console.log('[GOOGLE-OAUTH] Full URL:', window.location.href);
      console.log('[GOOGLE-OAUTH] Hash:', location.hash);
      console.log('[GOOGLE-OAUTH] Search:', location.search);

      try {
        console.log('[GOOGLE-OAUTH] 📡 Fetching session from Supabase...');
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('[GOOGLE-OAUTH] ❌ Session error:', sessionError);
          console.error('[GOOGLE-OAUTH] Error code:', sessionError.code);
          console.error('[GOOGLE-OAUTH] Error message:', sessionError.message);
          setError('Erro ao processar login com Google');
          toast.error('Erro ao processar login com Google');
          setIsProcessing(false);
          return;
        }

        console.log('[GOOGLE-OAUTH] Session data:', {
          hasSession: !!data.session,
          hasUser: !!data.session?.user,
          userId: data.session?.user?.id,
          email: data.session?.user?.email,
        });

        if (data.session) {
          console.log('[GOOGLE-OAUTH] ✅ Session found, reloading user...');
          toast.success('Login com Google realizado com sucesso!');
          await reload();
          setIsProcessing(false);
        } else {
          console.log('[GOOGLE-OAUTH] ⚠️ No session found in callback');
          console.log('[GOOGLE-OAUTH] This might indicate a configuration issue');
          setError('Nenhuma sessão encontrada');
          toast.error('Falha ao obter sessão. Verifique a configuração do Google OAuth.');
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('[GOOGLE-OAUTH] ❌ Error processing callback:', err);
        console.error('[GOOGLE-OAUTH] Error details:', {
          name: err instanceof Error ? err.name : 'Unknown',
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        setError('Erro ao processar callback');
        toast.error('Erro ao processar autenticação. Consulte o console para detalhes.');
        setIsProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [location, reload]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1b3b69' }}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/gcf_logo_01.png"
                alt="Grupo Cropfield"
                className="h-16 w-auto"
              />
            </div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Finalizando login...</h2>
            <p className="text-gray-600 text-sm">Aguarde enquanto verificamos suas credenciais do Google.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('[GOOGLE-OAUTH] Error detected, redirecting to login');
    return <Navigate to="/login?error=google_auth_failed" replace />;
  }

  if (isAuthenticated) {
    console.log('[GOOGLE-OAUTH] User authenticated, redirecting to dashboard');
    return <Navigate to="/" replace />;
  }

  return <Navigate to="/login" replace />;
};

export default GoogleOAuth;